import { createFileRoute, Link, Outlet, useParams, useLocation } from '@tanstack/react-router';
import { useStore, useIsLoaded } from '../../store';
import { useAppContext } from '../../contexts/AppContext';
import { CardSkeleton } from '../../components/skeletons';
import styles from '../../components/ProjectsView.module.css';

export const Route = createFileRoute('/projects/$projectId')({
  component: ProjectDetailLayout,
});

function ProjectDetailLayout() {
  const { projectId } = useParams({ from: '/projects/$projectId' });
  const location = useLocation();
  const { errorMessage, setErrorMessage } = useAppContext();
  const projects = useStore(s => s.projects);
  const organization = useStore(s => s.organization);
  const project = projects[projectId];
  const projectsLoaded = useIsLoaded('projects');

  // Check if we're on a detail page (app detail or profile detail)
  // These pages render their own full layout
  const isDetailPage = /\/applications\/[^/]+$/.test(location.pathname) ||
                       /\/profiles\/[^/]+$/.test(location.pathname);

  // Show skeleton while loading
  if (!projectsLoaded) {
    return (
      <div className={styles.container}>
        <div className={styles.compactHeader}>
          <div className={styles.breadcrumb}>
            <Link to="/projects" className={styles.breadcrumbLink}>
              Projects
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>Loading...</span>
          </div>
        </div>
        <CardSkeleton />
      </div>
    );
  }

  // Show error only after data is loaded and project truly doesn't exist
  if (!project) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          <span className={styles.errorText}>Project not found</span>
          <Link to="/projects" className={styles.errorClose}>
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // For detail pages, just render the outlet without the project wrapper
  if (isDetailPage) {
    return <Outlet />;
  }

  return (
    <div className={styles.container}>
      {/* Compact header: breadcrumb + tabs inline */}
      <div className={styles.compactHeader}>
        <div className={styles.breadcrumb}>
          <Link to="/projects" className={styles.breadcrumbLink}>
            Projects
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbCurrent}>{project.name}</span>
        </div>
        <div className={styles.tabList}>
          <Link
            to="/projects/$projectId/overview"
            params={{ projectId }}
            className={styles.tab}
            activeProps={{ className: `${styles.tab} ${styles.active}` }}
          >
            overview
          </Link>
          <Link
            to="/projects/$projectId/applications"
            params={{ projectId }}
            className={styles.tab}
            activeProps={{ className: `${styles.tab} ${styles.active}` }}
          >
            applications
          </Link>
          {organization?.planTier?.allowProfiles ? (
            <Link
              to="/projects/$projectId/profiles"
              params={{ projectId }}
              className={styles.tab}
              activeProps={{ className: `${styles.tab} ${styles.active}` }}
            >
              profiles
            </Link>
          ) : (
            <span className={styles.tabDisabled}>profiles</span>
          )}
          <Link
            to="/projects/$projectId/share-links"
            params={{ projectId }}
            className={styles.tab}
            activeProps={{ className: `${styles.tab} ${styles.active}` }}
          >
            share links
          </Link>
          <Link
            to="/projects/$projectId/admission-tokens"
            params={{ projectId }}
            className={styles.tab}
            activeProps={{ className: `${styles.tab} ${styles.active}` }}
          >
            admission tokens
          </Link>
          {/* Show domains tab if custom domains are enabled for this project */}
          {(project as any).customDomainsEnabled && (
            <Link
              to="/projects/$projectId/domains"
              params={{ projectId }}
              className={styles.tab}
              activeProps={{ className: `${styles.tab} ${styles.active}` }}
            >
              domains
            </Link>
          )}
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            className={styles.tab}
            activeProps={{ className: `${styles.tab} ${styles.active}` }}
          >
            settings
          </Link>
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>
          <span className={styles.errorText}>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className={styles.errorClose}>
            ×
          </button>
        </div>
      )}

      {/* Tab Content */}
      <Outlet />
    </div>
  );
}
